import { Feature } from '@client/graphql/types/graphql.ts';
import { useCompany } from '@packages/core/company/hook/use-company.ts';
import CardTableContent from '@packages/ui/card/card-content-table.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import { ContentLayout, PrimaryContent } from '@packages/ui/content-layout.tsx';
import Loading from '@packages/ui/loading.tsx';
import { useTranslate } from '@tolgee/react';
import { useParams } from 'react-router-dom';
import { useNotificationCenter } from 'ui/src/notification/notification-center';
import Spinner from 'ui/src/spinner';
import FeatureEnable from './component/feature-enable';
import OnOffButton from './component/on-off-button';
import { useSetFeature } from './logic/use-set-feature';

const services: { category: Feature; item: Feature[] }[] = [
  {
    category: Feature.LocalOrder,
    item: [],
  },
  {
    category: Feature.TableReservation,
    item: [Feature.TableReservationCapacity],
  },
  {
    category: Feature.NailAppointment,
    item: [Feature.NailAppointmentCapacity],
  },
  {
    category: Feature.OnlineOrder,
    item: [Feature.OnlineOrderCapacity],
  },
];

export default function CompanyFeature() {
  const companyId = useParams().companyId ?? '';
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const { data, loading } = useCompany(companyId);
  const [update, { loading: updateLoading }] = useSetFeature();

  if (loading) {
    return <Loading />;
  }

  if (!data?.company) {
    return <>Empty data</>;
  }

  const handleChange = (value: boolean, feature: Feature) => {
    update({
      variables: {
        companyId: companyId,
        name: feature,
        enabled: value,
      },
    })
      .then(() => {
        showAlert({
          status: 'success',
          title: t('alert.success'),
          message: t(`organisation.alert.${feature}`),
        });
        // navigate('..');
      })
      .catch((err: Error) => {
        showAlert({
          status: 'error',
          title: t('alert.error'),
          message: err.message,
        });
      });
  };

  return (
    <ContentLayout>
      <PrimaryContent>
        <Card>
          <CardHeader title={data.company.name} withBackButton={true}>
            {updateLoading && <Spinner />}
          </CardHeader>

          <CardTableContent>
            {services.map((item) => {
              const enabled = data.company?.features?.find((featureData) => featureData.name == item.category)
                ? true
                : false;
              return (
                <div key={item.category} className="border-b py-6">
                  <div className="p-4 flex justify-between items-center -mt-4">
                    <p className="text-xl font-semibold">{t(`feature-enable.header.${item.category}`)}</p>

                    {item.category == Feature.LocalOrder && (
                      <div className="flex items-center gap-x-1">
                        <OnOffButton
                          value={enabled}
                          onChange={(value) => {
                            handleChange(value, item.category);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {item.item.map((feature, index) => {
                    const enabled = data.company?.features?.find((featureData) => featureData.name == feature)
                      ? true
                      : false;
                    return (
                      <div key={index}>
                        <FeatureEnable feature={feature} enabled={enabled} handleChange={handleChange} />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </CardTableContent>
        </Card>
      </PrimaryContent>
    </ContentLayout>
  );
}
